/**
 * ChargesTable Component
 *
 * Displays a summary table of the user's selected additional charges accumlated during the journey.
 * Each row shows the item name, quantity, unit price, and total price, with an option to remove the item.
 * The table also displays the total of all extra charges at the bottom.
 *
 * Props:
 * - chargesList: Array of charge items added by the user (from AdditionalChargesState).
 * - onRemove: Callback function invoked when a user clicks the remove icon on a row.
 * - country: Used to determine and display the appropriate currency symbol.
 *
 * Behavior:
 * - Renders a table only if there is at least one item in the list.
 * - Dynamically calculates and displays the unit price and total per item.
 * - Calculates and shows the overall total at the bottom.
 * - Includes a remove icon for each row to allow removal of selected charges.
 */

import React from "react";
import { AdditionalChargesState } from "./additionalChargesReducer";
import removeIcon from '@assets/images/removeButton.png';
import { countrySpecificCurrency } from "@utils/misc/functions";

interface ChargesTableProps {
  chargesList: AdditionalChargesState["chargesList"];
  onRemove: (index: number) => void;
  country: string;
}

const ChargesTable: React.FC<ChargesTableProps> = ({ chargesList, onRemove, country }) => {
  const getTotalAmount = () => {
    return chargesList.reduce((total, item) => {
      const unit = parseFloat(item.addonTotalPrice || "0");
      return total + unit;
    }, 0).toFixed(2);
  };

  return (
    <div style={styles.wrapper}>
      {chargesList.length > 0 && (
        <table style={styles.table}>
          <colgroup>
            <col style={{ width: "45%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "15%" }} />
          </colgroup>
          <thead>
            <tr>
              <th style={styles.th}>Item</th>
              <th style={styles.th}>Qty</th>
              <th style={styles.th}>Unit ({countrySpecificCurrency(country)})</th>
              <th style={styles.th}>Total ({countrySpecificCurrency(country)})</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {chargesList.map((item, index) => {
              return (
                <tr key={`${item.addonId}-${index}`}>
                  <td style={styles.td}>{item.addonLabel}</td>
                  <td style={styles.td}>{item.addonQuantity}</td>
                  <td style={styles.td}>{item.addonUnitPrice}</td>
                  <td style={styles.td}>{parseFloat(item.addonTotalPrice ).toFixed(2)}</td>
                  <td style={styles.td}>
                    <img
                      src={removeIcon}
                      alt="Remove"
                      onClick={() => onRemove(index)}
                      style={styles.removeIcon}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <div style={styles.totalRow}>
        <strong>Total extra charges ({countrySpecificCurrency(country)})</strong>
        <span>{getTotalAmount()}</span>
      </div>
    </div>
  );
};

export default ChargesTable;

const styles: { [key: string]: React.CSSProperties } = {
  wrapper: {
    width: "100%",
    marginBottom: "20px",
    overflowX: "auto", // this may not be necessary but left in for safety
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed", // Ensures column widths are respected otherwise headings/content dont line up
  },
  th: {
    textAlign: "left",
    fontWeight: "bold",
    padding: "8px",
    borderBottom: "1px solid #ccc",
  },
  td: {
    padding: "8px",
    borderBottom: "1px solid #eee",
    verticalAlign: "middle",
  },
  removeIcon: {
    width: "16px",
    height: "16px",
    cursor: "pointer",
    display: "block",
    margin: "0 auto",
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    paddingTop: "12px",
    fontWeight: "bold",
  },
};
